import { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";

const inputClass =
  "w-full min-w-[150px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const textareaClass =
  "w-full min-w-[220px] resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const STATUS_OPTIONS = ["Pass", "Fail"];
const COMPLIANCE_OPTIONS = ["Fully", "Partially", "Non"];
const OWNERSHIP_OPTIONS = ["OEM", "F1", "Third Party"];

// Text input with a suggestion list. The full list opens on focus (or via the
// arrow) and narrows as the person types; free text is still accepted. The
// list is positioned with `fixed` so it is not clipped by the table's
// scrolling container, and it flips upward near the bottom of the screen.
function SuggestInput({ value, options, onChange, placeholder }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [pos, setPos] = useState(null);

  const needle = String(value || "").trim().toLowerCase();
  const visible =
    typed && needle
      ? options.filter((option) => option.toLowerCase().includes(needle))
      : options;

  const openList = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) {
      const flipUp = window.innerHeight - rect.bottom < 160;
      setPos({
        left: rect.left,
        width: rect.width,
        ...(flipUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    setOpen(true);
  };

  // The floating list would drift away from its input, so close it if the
  // page or the table scrolls or the window is resized.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const pick = (option) => {
    onChange(option);
    setOpen(false);
    setTyped(false);
  };

  return (
    <div
      ref={wrapRef}
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
          setTyped(false);
        }
      }}
    >
      <input
        type="text"
        autoComplete="off"
        value={value}
        placeholder={placeholder}
        className={`${inputClass} pr-9`}
        onFocus={() => {
          setTyped(false);
          openList();
        }}
        onChange={(event) => {
          setTyped(true);
          openList();
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
        }}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Show suggestions"
        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-500"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => {
          setTyped(false);
          if (open) setOpen(false);
          else openList();
        }}
      >
        <ChevronDown size={16} />
      </button>

      {open && pos && visible.length > 0 && (
        <div
          style={pos}
          className="fixed z-50 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {visible.map((option) => (
            <button
              key={option}
              type="button"
              className={`block w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                option === value ? "font-semibold text-blue-700" : "text-slate-700"
              }`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function SectionCard({
  title,
  description,
  buttonText,
  onAdd,
  children,
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>

          {description && (
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          )}
        </div>

        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
        >
          <Plus size={17} />
          {buttonText}
        </button>
      </div>

      <div className="p-5">{children}</div>
    </section>
  );
}

function EmptyState({ message }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
}

function RemoveButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100"
      title="Remove row"
    >
      <Trash2 size={16} />
    </button>
  );
}

export default function EligibilityFrameworkForm({ data, onChange }) {
  const updateRow = (section, index, field, value) => {
    const updatedRows = data[section].map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            [field]: value,
          }
        : row
    );

    onChange({
      ...data,
      [section]: updatedRows,
    });
  };

  const addRow = (section, newRow) => {
    onChange({
      ...data,
      [section]: [
        ...data[section],
        {
          id: createRowId(),
          ...newRow,
        },
      ],
    });
  };

  const removeRow = (section, index) => {
    onChange({
      ...data,
      [section]: data[section].filter(
        (_, rowIndex) => rowIndex !== index
      ),
    });
  };

  return (
    <div className="space-y-6">
      {/* <div className="rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
        <h3 className="font-semibold text-blue-950">
          Eligibility Framework
        </h3>

        <p className="mt-1 text-sm leading-6 text-blue-700">
          Record all pre-qualification, technical qualification, OEM and
          scope-of-work requirements from the RFP. Add or remove rows whenever
          the tender contains additional criteria.
        </p>
      </div> */}

      {/* PART A */}
      <SectionCard
        title="Part A: Pre-Qualification Criteria"
        description="Financial, legal, experience and organizational eligibility requirements."
        buttonText="Add PQ Criterion"
        onAdd={() =>
          addRow("preQualification", {
            criteria: "",
            compliantDetails: "",
            status: "",
            remarks: "",
          })
        }
      >
        {data.preQualification.length === 0 ? (
          <EmptyState message="No pre-qualification criteria added." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    S.No.
                  </th>

                  <th className="min-w-[320px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    PQ Criteria Description
                  </th>

                  <th className="min-w-[260px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    F1 Compliant Details
                  </th>

                  <th className="min-w-[170px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Status
                  </th>

                  <th className="min-w-[300px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Remarks / Evidence
                  </th>

                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 bg-white">
                {data.preQualification.map((row, index) => (
                  <tr key={row.id || index} className="align-top">
                    <td className="px-3 py-3 text-center text-sm font-semibold text-slate-600">
                      {index + 1}
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.criteria}
                        onChange={(event) =>
                          updateRow(
                            "preQualification",
                            index,
                            "criteria",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter criterion from the RFP"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.compliantDetails}
                        onChange={(event) =>
                          updateRow(
                            "preQualification",
                            index,
                            "compliantDetails",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter F1 compliance details"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <SuggestInput
                        value={row.status}
                        options={STATUS_OPTIONS}
                        onChange={(value) =>
                          updateRow("preQualification", index, "status", value)
                        }
                        placeholder="Select or type status"
                      />
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.remarks}
                        onChange={(event) =>
                          updateRow(
                            "preQualification",
                            index,
                            "remarks",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Evidence required, document name or remarks"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3 text-center">
                      <RemoveButton
                        onClick={() =>
                          removeRow("preQualification", index)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* PART B */}
      <SectionCard
        title="Part B: Technical Qualification Criteria"
        description="Technical evaluation parameters, proposed solutions and compliance status."
        buttonText="Add TQ Criterion"
        onAdd={() =>
          addRow("technicalQualification", {
            parameter: "",
            proposedSolution: "",
            compliance: "",
            technicalRisk: "",
          })
        }
      >
        {data.technicalQualification.length === 0 ? (
          <EmptyState message="No technical qualification criteria added." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    S.No.
                  </th>

                  <th className="min-w-[320px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    TQ Evaluation Parameter / Feature
                  </th>

                  <th className="min-w-[260px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Proposed OEM / Solution Layer
                  </th>

                  <th className="min-w-[180px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Compliance
                  </th>

                  <th className="min-w-[300px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Technical Risk / Justification
                  </th>

                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 bg-white">
                {data.technicalQualification.map((row, index) => (
                  <tr key={row.id || index} className="align-top">
                    <td className="px-3 py-3 text-center text-sm font-semibold text-slate-600">
                      {index + 1}
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.parameter}
                        onChange={(event) =>
                          updateRow(
                            "technicalQualification",
                            index,
                            "parameter",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter technical requirement"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.proposedSolution}
                        onChange={(event) =>
                          updateRow(
                            "technicalQualification",
                            index,
                            "proposedSolution",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter proposed OEM or solution"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <SuggestInput
                        value={row.compliance}
                        options={COMPLIANCE_OPTIONS}
                        onChange={(value) =>
                          updateRow("technicalQualification", index, "compliance", value)
                        }
                        placeholder="Select or type compliance"
                      />
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.technicalRisk}
                        onChange={(event) =>
                          updateRow(
                            "technicalQualification",
                            index,
                            "technicalRisk",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter technical risk or justification"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3 text-center">
                      <RemoveButton
                        onClick={() =>
                          removeRow("technicalQualification", index)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* PART C */}
      <SectionCard
        title="Part C: OEM Pre-Qualification Criteria"
        description="Financial, legal and authorization requirements applicable to the OEM."
        buttonText="Add OEM Criterion"
        onAdd={() =>
          addRow("oemPreQualification", {
            criteria: "",
            compliantDetails: "",
            status: "",
            remarks: "",
          })
        }
      >
        {data.oemPreQualification.length === 0 ? (
          <EmptyState message="No OEM pre-qualification criteria added." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    S.No.
                  </th>

                  <th className="min-w-[320px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    OEM PQ Criteria Description
                  </th>

                  <th className="min-w-[260px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    OEM Compliant Details
                  </th>

                  <th className="min-w-[170px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Status
                  </th>

                  <th className="min-w-[300px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Remarks / Evidence
                  </th>

                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 bg-white">
                {data.oemPreQualification.map((row, index) => (
                  <tr key={row.id || index} className="align-top">
                    <td className="px-3 py-3 text-center text-sm font-semibold text-slate-600">
                      {index + 1}
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.criteria}
                        onChange={(event) =>
                          updateRow(
                            "oemPreQualification",
                            index,
                            "criteria",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter OEM criterion"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.compliantDetails}
                        onChange={(event) =>
                          updateRow(
                            "oemPreQualification",
                            index,
                            "compliantDetails",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter OEM compliance details"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <SuggestInput
                        value={row.status}
                        options={STATUS_OPTIONS}
                        onChange={(value) =>
                          updateRow("oemPreQualification", index, "status", value)
                        }
                        placeholder="Select or type status"
                      />
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.remarks}
                        onChange={(event) =>
                          updateRow(
                            "oemPreQualification",
                            index,
                            "remarks",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Enter supporting evidence or remarks"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3 text-center">
                      <RemoveButton
                        onClick={() =>
                          removeRow("oemPreQualification", index)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* PART D */}
      <SectionCard
        title="Part D: Scope of Work"
        description="Break down the RFP scope, ownership, effort and applicable cost."
        buttonText="Add SOW Item"
        onAdd={() =>
          addRow("scopeOfWork", {
            sow: "",
            ownership: "",
            manDays: "",
            cost: "",
          })
        }
      >
        {data.scopeOfWork.length === 0 ? (
          <EmptyState message="No scope-of-work items added." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-slate-100">
                <tr>
                  <th className="w-16 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    S.No.
                  </th>

                  <th className="min-w-[350px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Scope of Work
                  </th>

                  <th className="min-w-[220px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Ownership
                  </th>

                  <th className="min-w-[150px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Number of Man-Days
                  </th>

                  <th className="min-w-[180px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Cost
                  </th>

                  <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 bg-white">
                {data.scopeOfWork.map((row, index) => (
                  <tr key={row.id || index} className="align-top">
                    <td className="px-3 py-3 text-center text-sm font-semibold text-slate-600">
                      {index + 1}
                    </td>

                    <td className="px-3 py-3">
                      <textarea
                        value={row.sow}
                        onChange={(event) =>
                          updateRow(
                            "scopeOfWork",
                            index,
                            "sow",
                            event.target.value
                          )
                        }
                        rows={3}
                        placeholder="Describe the scope item"
                        className={textareaClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <SuggestInput
                        value={row.ownership}
                        options={OWNERSHIP_OPTIONS}
                        onChange={(value) =>
                          updateRow("scopeOfWork", index, "ownership", value)
                        }
                        placeholder="Select or type ownership"
                      />
                    </td>

                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={row.manDays}
                        onChange={(event) =>
                          updateRow(
                            "scopeOfWork",
                            index,
                            "manDays",
                            event.target.value
                          )
                        }
                        placeholder="0"
                        className={inputClass}
                      />
                    </td>

                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.cost}
                        onChange={(event) =>
                          updateRow(
                            "scopeOfWork",
                            index,
                            "cost",
                            event.target.value
                          )
                        }
                        placeholder="₹0"
                        className={inputClass}
                      />
                    </td>

                    <td className="px-3 py-3 text-center">
                      <RemoveButton
                        onClick={() => removeRow("scopeOfWork", index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-slate-500">PQ Criteria</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {data.preQualification.length}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-slate-500">TQ Criteria</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {data.technicalQualification.length}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-slate-500">OEM Criteria</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {data.oemPreQualification.length}
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-slate-500">SOW Items</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {data.scopeOfWork.length}
          </p>
        </div>
      </div>
    </div>
  );
}